import { Component, Inject } from '@angular/core';
import { FormBuilder, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { TranslatePipe } from '../../core/i18n/translate.pipe';
import { EnterSubmitDirective } from '../../core/directives/enter-submit.directive';

export interface EditInstructorRatesDialogData {
  title: string;
  rates: Array<{ category: string; rate: number }>;
}

export interface EditInstructorRatesDialogResult {
  hourlyRateByCategory: Record<string, number>;
  rateChangeReason?: string;
}

@Component({
  selector: 'app-edit-instructor-rates-dialog',
  standalone: true,
  imports: [ReactiveFormsModule, MatDialogModule, MatButtonModule, MatFormFieldModule, MatInputModule, TranslatePipe, EnterSubmitDirective],
  template: `
    <h2 mat-dialog-title>{{ data.title }}</h2>
    <form appEnterSubmit [formGroup]="form" (ngSubmit)="apply()">
    <mat-dialog-content>
      @if (data.rates.length > 0) {
        <div class="rates-form">
          @for (item of data.rates; track item.category; let index = $index) {
            <mat-form-field>
              <mat-label>{{ item.category }} (PLN/h)</mat-label>
              <input matInput type="number" min="0" step="1" [formControlName]="controlKey(index)" />
            </mat-form-field>
          }
          <mat-form-field class="reason-field">
            <mat-label>{{ 'field.rateChangeReason' | t }}</mat-label>
            <textarea matInput rows="3" formControlName="rateChangeReason"></textarea>
          </mat-form-field>
        </div>
      } @else {
        <p class="empty-message">{{ 'resourceDetails.noInstructorRateCategories' | t }}</p>
      }
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button type="button" (click)="dialogRef.close(null)">{{ 'common.cancel' | t }}</button>
      <button mat-flat-button type="submit" color="primary" [disabled]="form.invalid">{{ 'common.apply' | t }}</button>
    </mat-dialog-actions>
    </form>
  `,
  styles: `
    form { display: contents; }
    .rates-form {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 12px;
      min-width: 420px;
      margin-top: 8px;
    }
    .reason-field {
      grid-column: 1 / -1;
    }
    .empty-message {
      min-width: 320px;
      margin: 8px 0 0;
      color: #52657d;
    }
    @media (max-width: 700px) {
      .rates-form {
        grid-template-columns: 1fr;
        min-width: 280px;
      }
    }
  `
})
export class EditInstructorRatesDialogComponent {
  readonly form;

  constructor(
    private readonly fb: FormBuilder,
    readonly dialogRef: MatDialogRef<EditInstructorRatesDialogComponent, EditInstructorRatesDialogResult | null>,
    @Inject(MAT_DIALOG_DATA) readonly data: EditInstructorRatesDialogData
  ) {
    const controls: Record<string, FormControl<number> | FormControl<string>> = {
      rateChangeReason: this.fb.nonNullable.control('')
    };
    data.rates.forEach((item, index) => {
      controls[this.controlKey(index)] = this.fb.nonNullable.control(item.rate, [
        Validators.required,
        Validators.min(0)
      ]);
    });
    this.form = this.fb.group(controls);
  }

  controlKey(index: number): string {
    return `rate_${index}`;
  }

  apply(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const raw = this.form.getRawValue();
    const next: Record<string, number> = {};
    this.data.rates.forEach((item, index) => {
      const key = this.controlKey(index);
      const value = Number(raw[key]);
      if (Number.isFinite(value) && value >= 0) {
        next[item.category] = value;
      }
    });
    const reason = `${raw['rateChangeReason'] ?? ''}`.trim();
    this.dialogRef.close({
      hourlyRateByCategory: next,
      ...(reason ? { rateChangeReason: reason } : {})
    });
  }
}
